import { Button, Grid, ScrollView, SearchField } from "@aws-amplify/ui-react";

import { useMemo, useState } from "react";
import LocalizedStrings from "react-localization";
import moment from "moment";
import {
  CCard,
  CCardBody,
  CCollapse,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CTable,
} from "@coreui/react";

import ConfirmModal from "components/utils/ConfirmModal";
import ActionIcon from "components/actions/ActionIcon";
import demoInputs from "config/demo/inputs.json";
import InputCreateForm from "./InputCreateForm";
import InputUpdateForm from "./InputUpdateForm";

const Inputs = () => {
  const [inputs] = useState(demoInputs.slice(0, 10));
  const [inputDetails, setInputDetails] = useState();
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    inputId: null,
    inputName: null,
  });
  const [modalInput, setModalInput] = useState({
    visible: false,
    inputId: null,
  });
  const columnsMapped = useMemo(
    () =>
      inputs.map((item) => ({
        ...item,
        actions: (
          <>
            <ActionIcon
              name={
                inputDetails?.id === item.id
                  ? strings.inputs.hide
                  : strings.inputs.show
              }
              icon={
                inputDetails?.id === item.id ? "VisibilityOff" : "Visibility"
              }
              color="primary"
              onClick={() => {
                inputDetails?.id === item.id
                  ? setInputDetails(null)
                  : setInputDetails(item);
              }}
            />
            <ActionIcon
              name={strings.inputs.edit}
              icon="Edit"
              color="primary"
              onClick={() => openModal("update", item.id)}
            />
            <ActionIcon
              name={strings.inputs.delete}
              icon="Delete"
              color="red.60"
              onClick={() => {
                setModalDetails({
                  visible: true,
                  inputId: item.id,
                  inputName: item.name,
                });
              }}
            />
          </>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inputs, inputDetails?.id]
  );

  const openModal = (action, id) =>
    setModalInput({ visible: true, action, inputId: id });

  const tableColumns = [
    {
      key: "name",
      label: strings.table.headers.name,
      _style: { width: "20%" },
    },
    {
      key: "activeIngredient",
      label: strings.table.headers.activeIngredient,
      _style: { width: "20%" },
    },
    {
      key: "unit",
      label: strings.table.headers.unit,
      _style: { width: "10%" },
    },
    {
      key: "classification",
      label: strings.table.headers.classification,
      _style: { width: "20%" },
    },
    {
      key: "physicalType",
      label: strings.table.headers.physicalType,
      _style: { width: "10%" },
    },
    {
      key: "actions",
      label: "",
      _style: { width: "15%" },
    },
  ];

  return (
    <div>
      <span className="fs-3">{strings.title}</span>
      <Button
        style={styles.buttonAdd}
        children={strings.inputs.addInput}
        variation="primary"
        onClick={() => openModal("create")}
      />
      <div className="p-3">
        <Grid
          className="mb-3"
          templateColumns={{ medium: "1fr", xl: "1fr 1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <SearchField
            label={strings.table.search.label}
            placeholder={strings.table.search.placeholder}
            // onSubmit={onKeywordSubmit}
            // onClear={onKeywordSubmit}
          />
        </Grid>
        <>
          <CTable
            className="table-centered"
            items={columnsMapped}
            columns={tableColumns}
            tableHeadProps={{ color: "light" }}
            responsive
            hover
          />
        </>
        <CCollapse visible={!!inputDetails?.id} style={styles.collapseCard}>
          <CCard key={inputDetails?.id}>
            <CCardBody>
              <h4 className="text-muted">
                {strings.details.title} "{inputDetails?.name}"
              </h4>

              <p className="text-muted">
                <b>{strings.details.name}:</b> {inputDetails?.name}
              </p>

              <p className="text-muted">
                <b>{strings.details.activeIngredient}:</b>{" "}
                {inputDetails?.activeIngredient}
              </p>

              <p className="text-muted">
                <b>{strings.details.unit}:</b> {inputDetails?.unit}
              </p>

              <p className="text-muted">
                <b>{strings.details.classification}:</b>{" "}
                {inputDetails?.classification}
              </p>

              <p className="text-muted">
                <b>{strings.details.physicalType}:</b>{" "}
                {inputDetails?.physicalType}
              </p>

              <p className="text-muted">
                <b>{strings.details.icaRegistration}:</b>{" "}
                {inputDetails?.icaRegistration}
              </p>

              <p className="text-muted">
                <b>{strings.details.toxicologicalCategory}:</b>{" "}
                {inputDetails?.toxicologicalCategory}
              </p>

              <p className="text-muted">
                <b>{strings.details.createdAt}:</b>{" "}
                {inputDetails?.createdAt
                  ? moment(inputDetails?.createdAt).format("YYYY-MM-DD HH:mm:ss")
                  : "-"}
              </p>
            </CCardBody>
          </CCard>
        </CCollapse>
      </div>
      <ConfirmModal
        visible={modalDetails.visible}
        body={strings.formatString(strings.modal.confirmDeleteQuestion, {
          name: modalDetails.inputName,
        })}
        onCancel={() => setModalDetails({ visible: false, inputId: null })}
        // onConfirm={() => inputDelete(modalDetails.inputId)}
      />
      {modalInput.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalInput.visible}
          onClose={() => setModalInput({ visible: false })}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              {modalInput.action === "create" && strings.modalInput.addTitle}
              {modalInput.action === "update" && strings.modalInput.updateTitle}
            </CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              {modalInput.action === "create" && (
                <InputCreateForm onSuccess={() => {}} />
              )}
              {modalInput.action === "update" && (
                <InputUpdateForm
                  inputId={modalInput.inputId}
                  onSuccess={() => {}}
                />
              )}
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Insumos",
    successDeleted: "Insumo eliminado con éxito",
    inputs: {
      addInput: "Agregar Insumo",
      show: "Ver",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
    },
    table: {
      headers: {
        name: "Nombre",
        activeIngredient: "Ingrediente activo",
        unit: "Unidad",
        classification: "Clasificación",
        physicalType: "Tipo",
        actions: "Acciones",
      },
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    details: {
      title: "Detalles del Insumo",
      name: "Nombre",
      activeIngredient: "Ingrediente activo",
      unit: "Unidad",
      classification: "Clasificación",
      physicalType: "Tipo",
      icaRegistration: "Registro ICA",
      toxicologicalCategory: "Categoría Toxicológica",
      createdAt: "Fecha de creación",
    },
    modal: {
      confirmDeleteQuestion: "¿Estás seguro de eliminar el insumo {name}?",
      cancelButton: "Cancelar",
      confirmButton: "Confirmar",
    },
    modalInput: {
      addTitle: "Agregar Insumo",
      updateTitle: "Actualizar Insumo",
    },
  },
});

const styles = {
  buttonAdd: { float: "right" },
  modal: { width: "250px" },
  collapseCard: { marginTop: "15px" },
  buttonPrevious: { marginLeft: "15px" },
  buttonNext: { float: "right", marginRight: "15px" },
  searchByKeyword: { marginBottom: "15px", width: "25%", minWidth: "300px" },
};

export default Inputs;
